// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import clsx from 'clsx';
import { MessageBar, MessageBarType, Icon } from 'office-ui-fabric-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import iconUserHeader from './../../assets/icon-user-header.png';
import logoImage from './../../assets/microsoft@3x.png';
import { Link } from 'react-router-dom';
import UserConfigMenu from './../../components/UserConfigMenu';
import { sessionId, logPageAction } from 'src/lib/ApplicationInsights';
import CorrelationVector from 'src/lib/CorrelationVector';
import ErrorBoundaryContext from 'src/lib/ErrorBoundaryContext';

import Styles from './header.module.scss';
import Feedback from './../../components/Feedback/Feedback';

function Header(props: any) {
    const { t } = useTranslation();
    const [isFeedbackOpen, setIsFeedbackOpen] = React.useState<boolean>(false);

    const getErrorMessage = (cv?: string): JSX.Element => {
        return (
            <span>
                {t('something_went_wrong', { sessionId, operationId: cv || CorrelationVector.getPreviousCV() })}
            </span>
        );
    };

    function onFaqLinkClick() {
        logPageAction('Link to FAQ clicked');
    }

    function onFeedbackClick() {
        logPageAction(`Link to feedback clicked, feedback dialog is ${isFeedbackOpen ? 'open' : 'close'} now`);
        setIsFeedbackOpen(!isFeedbackOpen);
    }

    return (
        <ErrorBoundaryContext.Consumer>
            {({ correlationVector, error, onDismiss, onDismissBrowserWarningMessage, showBrowserWarningMessage }) => {
                if (error) {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                }

                return (
                    <>
                        {/* If no dismiss function provided, must be fatal error, prevent clicking on buttons */}
                        <header className={clsx(...[Styles.header, ...(!onDismiss ? [Styles.unclickable] : [])])}>
                            <div className={Styles.logo}>
                                <a aria-label={t('robo_logo_text')} href={window.location.origin}>
                                    <img src={logoImage} alt="Robo Site Logo" className={Styles.msLogo} />
                                    <span>{t('robo_logo_text')}</span>
                                </a>
                                <span className={Styles.envBadge}>UAT</span>
                            </div>
                            <div onClick={onFaqLinkClick} className={Styles.logoRight}>
                                <Link to="/faqs" aria-label={t('frequently_asked_questions')} title={t('frequently_asked_questions')}>
                                    <Icon iconName="StatusCircleQuestionMark" className={Styles.img} />
                                </Link>

                                <Feedback
                                    isOpen={isFeedbackOpen}
                                    onClose={onFeedbackClick}
                                >
                                    <a
                                        href="#"
                                        role="button"
                                        className={Styles.feedback}
                                        aria-label={t('feedback')}
                                        onClick={onFeedbackClick}
                                        title={t('feedback')}
                                    >
                                        <Icon iconName="Emoji2" />
                                    </a>
                                </Feedback>
                                <UserConfigMenu />
                                <img src={iconUserHeader} alt="User" className={Styles.userImg} />
                            </div>
                        </header>
                        <div className={Styles.messageBarContainer}>
                            {
                                error
                                    ? (
                                        // If no dismiss function provided, set value to null to hide the close button
                                        <MessageBar
                                            isMultiline={true}
                                            messageBarType={MessageBarType.error}
                                            // @ts-ignore
                                            onDismiss={onDismiss || null}
                                        >
                                            {getErrorMessage(correlationVector)}
                                        </MessageBar>
                                    ) : null
                            }
                            {
                                showBrowserWarningMessage
                                    ? (
                                        <MessageBar
                                            className={Styles.warning}
                                            messageBarType={MessageBarType.warning}
                                            onDismiss={onDismissBrowserWarningMessage}
                                        >
                                            {t('browser_may_not_be_supported')}
                                        </MessageBar>
                                    ) : null
                            }
                        </div >
                    </>
                );
            }}
        </ErrorBoundaryContext.Consumer>
    );
}

export default Header;